
import { Module } from '../types';

export const CONCURRENT_FEATURES_MODULE: Module = {
  id: "advanced-3",
  title: "React Concurrent Features",
  description: "Learn about React's concurrent rendering capabilities",
  duration: "75 minutes",
  difficulty: "advanced", // Add missing difficulty
  content: `
# React Concurrent Features

React 18 introduced powerful concurrent features that improve user experience by making applications more responsive.

## Concurrent Rendering

Understanding how React can work on multiple tasks simultaneously.

## Suspense for Data Fetching

Use Suspense to handle loading states declaratively.

## Transitions

Mark updates as non-urgent to keep the UI responsive.

## useDeferredValue

Defer expensive computations to improve performance.
  `,
  codeExamples: [
    {
      title: "Using Suspense and Transitions",
      code: `import React, { Suspense, useState, useTransition } from 'react';

function SearchResults({ query }) {
  // This component might suspend while fetching data
  const results = useFetch(\`/api/search?q=\${query}\`);
  
  return (
    <div>
      {results.map(result => (
        <div key={result.id}>{result.title}</div>
      ))}
    </div>
  );
}

function App() {
  const [query, setQuery] = useState('');
  const [isPending, startTransition] = useTransition();

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Mark search as non-urgent
    startTransition(() => {
      setSearchQuery(value);
    });
  };

  return (
    <div>
      <input 
        value={query}
        onChange={handleInputChange}
        placeholder="Search..."
      />
      {isPending && <div>Searching...</div>}
      <Suspense fallback={<div>Loading results...</div>}>
        <SearchResults query={query} />
      </Suspense>
    </div>
  );
}`,
      explanation: "Example showing Suspense for loading states and useTransition for non-urgent updates.",
      language: "jsx"
    }
  ],
  practice: {
    task: "Implement a search interface with concurrent features",
    starter: `import React, { useState, useTransition, Suspense } from 'react';

function SearchApp() {
  // Advanced search with transitions and suspense
  function SearchWithTransitions() {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isPending, startTransition] = useTransition();
    const [isLoading, setIsLoading] = useState(false);
    
    // Simulated data source
    const mockData = [
      { id: 1, title: 'React Hooks', category: 'frontend', content: 'State management with hooks' },
      { id: 2, title: 'Node.js API', category: 'backend', content: 'Building REST APIs' },
      { id: 3, title: 'TypeScript', category: 'language', content: 'Type-safe JavaScript' },
      { id: 4, title: 'React Query', category: 'frontend', content: 'Data fetching library' },
      { id: 5, title: 'Express.js', category: 'backend', content: 'Web framework for Node.js' },
      { id: 6, title: 'GraphQL', category: 'api', content: 'Query language for APIs' },
    ];
    
    // Simulated search function with delay
    const performSearch = async (term) => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
      
      const filtered = mockData.filter(item =>
        item.title.toLowerCase().includes(term.toLowerCase()) ||
        item.content.toLowerCase().includes(term.toLowerCase()) ||
        item.category.toLowerCase().includes(term.toLowerCase())
      );
      
      setResults(filtered);
      setIsLoading(false);
    };
    
    // Handle search with transitions
    const handleSearch = (e) => {
      const term = e.target.value;
      setSearchTerm(term);
      
      if (term.trim()) {
        startTransition(() => {
          performSearch(term);
        });
      } else {
        setResults([]);
      }
    };
    
    return (
      <div className="search-container">
        <div className="search-input-container">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search tutorials, topics, or categories..."
            className="search-input"
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '16px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
          />
          {(isPending || isLoading) && (
            <div className="search-loading" style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b'
            }}>
              Searching...
            </div>
          )}
        </div>
        
        <Suspense fallback={<SearchSkeleton />}>
          <SearchResults results={results} searchTerm={searchTerm} />
        </Suspense>
      </div>
    );
  }
  
  // Skeleton component for loading state
  function SearchSkeleton() {
    return (
      <div className="search-skeleton">
        {[...Array(3)].map((_, i) => (
          <div key={i} style={{
            height: '80px',
            backgroundColor: '#f1f5f9',
            borderRadius: '6px',
            margin: '8px 0',
            animation: 'pulse 1.5s ease-in-out infinite'
          }} />
        ))}
      </div>
    );
  }
  
  // Results component wrapped in suspense
  function SearchResults({ results, searchTerm }) {
    if (!searchTerm) {
      return (
        <div className="search-empty" style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
          Type to search for tutorials and resources
        </div>
      );
    }
    
    if (results.length === 0) {
      return (
        <div className="search-no-results" style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
          No results found for "{searchTerm}"
        </div>
      );
    }
    
    return (
      <div className="search-results" style={{ marginTop: '16px' }}>
        <div style={{ marginBottom: '12px', color: '#64748b', fontSize: '14px' }}>
          Found {results.length} result{results.length !== 1 ? 's' : ''} for "{searchTerm}"
        </div>
        {results.map((result) => (
          <div key={result.id} style={{
            padding: '16px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            marginBottom: '8px',
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#f8fafc';
            e.target.style.borderColor = '#3b82f6';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.borderColor = '#e2e8f0';
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
              {result.title}
            </h3>
            <p style={{ margin: '0 0 8px 0', color: '#64748b', fontSize: '14px' }}>
              {result.content}
            </p>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              backgroundColor: '#e0f2fe',
              color: '#0369a1',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {result.category}
            </span>
          </div>
        ))}
      </div>
    );
  }
}

export default SearchApp;`,
    solution: `import React, { useState, useTransition, Suspense } from 'react';

function SearchResults({ query }) {
  // Simulate data fetching that might suspend
  if (!query) return <div>Enter a search term</div>;
  
  return (
    <div>
      <h3>Results for: {query}</h3>
      <div>Sample result 1</div>
      <div>Sample result 2</div>
    </div>
  );
}

function SearchApp() {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [isPending, startTransition] = useTransition();

  const handleSearch = (value) => {
    setInput(value);
    startTransition(() => {
      setQuery(value);
    });
  };

  return (
    <div>
      <input 
        value={input}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder="Search..."
      />
      {isPending && <div>Searching...</div>}
      <Suspense fallback={<div>Loading...</div>}>
        <SearchResults query={query} />
      </Suspense>
    </div>
  );
}

export default SearchApp;`,
    hints: [
      "Use useTransition to mark search updates as non-urgent",
      "Wrap async components with Suspense",
      "Show loading states during transitions",
      "Keep input responsive while search is processing"
    ]
  }
};
